import { toast } from 'react-toastify';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaSpinner, FaExclamationTriangle, FaSearch } from 'react-icons/fa';
import PageFooter from '../../molecules/PageFooter';
import { BatchProcessing } from './types';
import { listBatchProcessing, BatchListItem } from '../../../services/CreditAnalysisService';

const POLL_INTERVAL_MS = 30000;
const SEARCH_DEBOUNCE_MS = 400;

const adaptBatch = (item: BatchListItem): BatchProcessing => ({
    id: item.id,
    uuid: item.id,
    batchNumber: item.batchNumber,
    batchName: item.batchName,
    status: item.status === 'COMPLETED' ? 'completed'
          : item.status === 'ERROR' ? 'error'
          : 'processing',
    createdAt: item.createdAt,
    approvedCount: item.approvedCount || 0,
    rejectedCount: item.rejectedCount || 0,
    pendingCount: item.pendingCount || 0,
    errorCount: item.errorCount || 0,
    totalItems: item.totalItems || 0,
    assignors: [{ name: item.assignorName, drawees: [] }],
} as unknown as BatchProcessing);

const isAbortError = (err: unknown): boolean => err instanceof DOMException && err.name === 'AbortError';

export default function BatchProcessingList() {
    const navigate = useNavigate();
    const [batches, setBatches] = useState<BatchProcessing[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Monotonic id of the latest request; responses from stale requests are ignored.
    const requestIdRef = useRef(0);
    // AbortController of the current visible request, so polling/newer requests can cancel it.
    const abortRef = useRef<AbortController | null>(null);

    const fetchBatches = useCallback((showSpinner: boolean) => {
        // Any new fetch supersedes the in-flight one (visible or polling).
        abortRef.current?.abort();
        if (showSpinner) setFetching(true);
        const requestId = ++requestIdRef.current;
        const controller = new AbortController();
        abortRef.current = controller;

        listBatchProcessing(currentPage, itemsPerPage, debouncedSearch, controller.signal)
            .then(data => {
                // Ignore responses that are no longer the latest request.
                if (requestId !== requestIdRef.current) return;
                setBatches(data.content.map(adaptBatch));
                setTotalElements(data.totalElements);
                setTotalPages(data.totalPages);
            })
            .catch(err => {
                if (isAbortError(err) || requestId !== requestIdRef.current) return;
                toast.error('Erro ao carregar lotes');
            })
            .finally(() => {
                if (requestId !== requestIdRef.current) return;
                setLoading(false);
                setFetching(false);
                if (abortRef.current === controller) abortRef.current = null;
            });
    }, [currentPage, itemsPerPage, debouncedSearch]);

    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => {
        fetchBatches(true);
        // Silent background refresh; skipped while any fetch is still pending.
        const interval = setInterval(() => {
            if (abortRef.current) return;
            fetchBatches(false);
        }, POLL_INTERVAL_MS);
        return () => {
            clearInterval(interval);
            // Abort any in-flight request on unmount/param change; its (aborted)
            // response is ignored by the AbortError guard in the fetch handler.
            abortRef.current?.abort();
            abortRef.current = null;
        };
    }, [fetchBatches]);

    const getBatchStatusBadge = (status: 'processing' | 'error' | 'completed') => {
        const cfg = {
            processing: { style: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: <FaSpinner className="inline mr-1 animate-spin" />, label: 'Em Processamento' },
            error:      { style: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', icon: <FaExclamationTriangle className="inline mr-1" />, label: 'Erro' },
            completed:  { style: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: <FaCheckCircle className="inline mr-1" />, label: 'Concluído' },
        }[status];
        return <span className={`px-3 py-1 rounded text-sm font-medium ${cfg.style}`}>{cfg.icon} {cfg.label}</span>;
    };

    const formatDate = (dateString: string) =>
        new Date(dateString).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    const getTotalDrawees = (batch: BatchProcessing) =>
        (batch as any).totalItems || batch.assignors.reduce((t, a) => t + a.drawees.length, 0);

    const getStatusCounts = (batch: BatchProcessing) => {
        const c = { approved: (batch as any).approvedCount || 0, pending: (batch as any).pendingCount || 0, rejected: (batch as any).rejectedCount || 0, error: (batch as any).errorCount || 0 };
        return c;
    };

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 h-full flex flex-col">
            <div className="sticky top-0 bg-white dark:bg-slate-800 z-10 pb-4 pt-6">
                <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                    <input
                        type="text"
                        placeholder="Buscar por número ou nome do lote..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(0); }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100"
                    />
                </div>
            </div>

            <div className="flex justify-end items-center gap-4 text-xs text-gray-600 dark:text-gray-400 px-1 pb-2">
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" /> Aprovado</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" /> Rejeitado</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500" /> Pendente</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-violet-600" /> Erro</span>
            </div>

            <div className="flex-1 overflow-y-auto min-h-0 relative">
                {fetching && batches.length > 0 && (
                    <div className="absolute inset-0 z-20 flex items-start justify-center pt-8 bg-white/60 dark:bg-slate-800/60 backdrop-blur-[1px]">
                        <span className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-full px-3 py-1.5 shadow-sm">
                            <FaSpinner className="animate-spin text-blue-500" /> Carregando...
                        </span>
                    </div>
                )}
                <div className={`space-y-3 pb-4 transition-opacity ${fetching && batches.length > 0 ? 'opacity-50' : ''}`}>
                    {batches.map((batch) => {
                        const totalDrawees = getTotalDrawees(batch);
                        const counts = getStatusCounts(batch);
                        const assignorName = batch.assignors[0]?.name || '-';

                        return (
                            <div
                                key={batch.uuid}
                                className="bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => navigate(`/credit-analysis/batch/${batch.uuid}`)}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-1">
                                            <h3 className="text-base font-semibold dark:text-gray-100">{batch.batchName}</h3>
                                            {getBatchStatusBadge(batch.status)}
                                        </div>
                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                            Nº {batch.batchNumber} · Cedente: <span className="font-medium text-gray-800 dark:text-gray-200">{assignorName}</span> · {formatDate(batch.createdAt)}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-4 ml-4">
                                        <div className="flex items-center gap-3 text-xs">
                                            <span className="text-gray-700 dark:text-gray-300"><span className="font-semibold">{totalDrawees}</span> sacados</span>
                                            {counts.approved > 0 && <span className="flex items-center gap-1 text-green-700 dark:text-green-400"><span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" /> {counts.approved}</span>}
                                            {counts.rejected > 0 && <span className="flex items-center gap-1 text-red-700 dark:text-red-400"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> {counts.rejected}</span>}
                                            {counts.pending > 0 && <span className="flex items-center gap-1 text-yellow-700 dark:text-yellow-400"><span className="w-2.5 h-2.5 rounded-full bg-yellow-500 inline-block" /> {counts.pending}</span>}
                                            {counts.error > 0 && <span className="flex items-center gap-1 text-violet-700 dark:text-violet-400"><span className="w-2.5 h-2.5 rounded-full bg-violet-600 inline-block" /> {counts.error}</span>}
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); navigate(`/credit-analysis/batch/${batch.uuid}/analysis`); }}
                                            className="bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded px-3 py-1.5"
                                        >
                                            Analisar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {batches.length === 0 && (
                        <div className="text-center py-12">
                            {loading || fetching ? (
                                <>
                                    <FaSpinner className="inline mr-2 animate-spin text-blue-500 text-2xl mb-2" />
                                    <p className="text-lg text-gray-500 dark:text-gray-400">Carregando processamentos...</p>
                                </>
                            ) : (
                                <p className="text-2xl font-bold text-gray-500 dark:text-gray-400">
                                    {searchTerm ? 'Nenhum processamento encontrado para a busca' : 'Nenhum processamento encontrado'}
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {totalElements > 0 && (
                <div className="sticky bottom-0 border-t border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 flex-shrink-0 z-10">
                    <PageFooter
                        currentPage={currentPage}
                        totalPages={totalPages}
                        itemsPerPage={itemsPerPage}
                        totalElements={totalElements}
                        onPageChange={(page) => setCurrentPage(page)}
                        onItemsPerPageChange={(size) => { setItemsPerPage(size); setCurrentPage(0); }}
                        itemsLabel="processamentos"
                    />
                </div>
            )}
        </div>
    );
}
